import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { deleteDialog } from 'src/app/shared/dialog';
import {
  Agent,
  AGENT_PERMISSIONS,
  LlmModel,
  normalizePlaybookContent,
  Playbook,
  PLAYBOOK_CONTENT_MAX,
  PLAYBOOK_DESCRIPTION_MAX,
  PLAYBOOK_KEY_MAX,
  PLAYBOOK_TITLE_MAX,
  playbookErrorMessage,
  playbookFormProblem,
  PlaybookRequest,
  ProjectPlan,
} from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

export interface PlaybookForm {
  key: string;
  title: string;
  description: string;
  content: string;
  enabled: boolean;
}

/** POST body: a blank key is left out, so the service makes it from the title. */
export function buildPlaybookCreate(form: PlaybookForm): PlaybookRequest {
  const body: PlaybookRequest = {
    title: form.title.trim(),
    description: form.description.trim(),
    content: normalizePlaybookContent(form.content),
    enabled: form.enabled,
  };
  const key = (form.key || '').trim();
  if (key) body.key = key;
  return body;
}

/** PUT body: only the changed fields; null = unchanged (the key never changes here). */
export function buildPlaybookUpdate(original: Playbook, form: PlaybookForm): PlaybookRequest {
  const title = form.title.trim();
  const description = form.description.trim();
  const content = normalizePlaybookContent(form.content);
  return {
    key: null,
    title: title !== original.title ? title : null,
    description: description !== original.description ? description : null,
    content: content !== normalizePlaybookContent(original.content) ? content : null,
    enabled: form.enabled !== original.enabled ? form.enabled : null,
    sortOrder: null,
  };
}

/** True when a PUT body changes nothing. */
export function isEmptyUpdate(body: PlaybookRequest): boolean {
  return Object.values(body).every((v) => v === null || v === undefined);
}

/**
 * Contract v1.4 playbooks of one agent, for support (system scope, no plan checks): list without content,
 * view and edit with content, add, enable / disable, delete.
 */
@Component({
  selector: 'app-ai-agent-playbooks',
  templateUrl: './agent-playbooks.component.html',
  styleUrls: ['../../ai-agents.shared.css', './agent-playbooks.component.css'],
})
export class AgentPlaybooksComponent implements OnChanges {
  @Input() agent!: Agent;
  /** The plan of the agent's project, for the notes only; null = unknown. */
  @Input() plan: ProjectPlan | null = null;
  /** The agent's model, for the "cannot use playbooks" note; undefined = unknown. */
  @Input() model: LlmModel | undefined;

  readonly P = AGENT_PERMISSIONS;
  readonly titleMax = PLAYBOOK_TITLE_MAX;
  readonly descriptionMax = PLAYBOOK_DESCRIPTION_MAX;
  readonly contentMax = PLAYBOOK_CONTENT_MAX;
  readonly keyMax = PLAYBOOK_KEY_MAX;

  playbooks: Playbook[] = [];
  loading = false;
  loadError = '';
  listError = '';

  editorOpen = false;
  /** The playbook being viewed or edited (with content); null = a new one. */
  editing: Playbook | null = null;
  editorLoading = false;
  form: PlaybookForm = this.blankForm();
  formError = '';
  saving = false;
  /** Ids whose enable switch is being saved. */
  toggling: Record<string, boolean> = {};

  constructor(private api: AiAgentsApiService, public perms: AgentPermissionsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['agent'] && this.agent) {
      this.load();
    }
  }

  get canCreate(): boolean {
    return this.perms.has(this.P.playbookCreate);
  }

  get canUpdate(): boolean {
    return this.perms.has(this.P.playbookUpdate);
  }

  get canDelete(): boolean {
    return this.perms.has(this.P.playbookDelete);
  }

  /** In the dialog: the fields can be changed (create, or update permission). */
  get canEditForm(): boolean {
    return this.editing ? this.canUpdate : this.canCreate;
  }

  get planOff(): boolean {
    return !!this.plan && this.plan.allowPlaybooks === false;
  }

  get planMax(): number | null {
    return this.plan && typeof this.plan.maxPlaybooksPerAgent === 'number' ? this.plan.maxPlaybooksPerAgent : null;
  }

  get modelWithoutTools(): boolean {
    return !!this.model && this.model.supportsTools === false;
  }

  get enabledCount(): number {
    return this.playbooks.filter((p) => p.enabled).length;
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.api.listPlaybooks(this.agent.id).subscribe({
      next: (list) => {
        this.playbooks = list || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = playbookErrorMessage(err, 'Loading the playbooks failed.');
      },
    });
  }

  openCreate(): void {
    this.editing = null;
    this.form = this.blankForm();
    this.formError = '';
    this.editorLoading = false;
    this.editorOpen = true;
  }

  /** Opens the dialog and loads the playbook with its content. */
  openEdit(p: Playbook): void {
    this.editing = p;
    this.form = this.toForm(p);
    this.formError = '';
    this.editorLoading = true;
    this.editorOpen = true;
    this.api.getPlaybook(this.agent.id, p.id).subscribe({
      next: (full) => {
        if (this.editing?.id !== p.id) return;
        this.editing = full;
        this.form = this.toForm(full);
        this.editorLoading = false;
      },
      error: (err) => {
        this.editorLoading = false;
        this.formError = playbookErrorMessage(err, 'Loading the playbook failed.');
      },
    });
  }

  closeEditor(): void {
    this.editorOpen = false;
    this.editing = null;
  }

  /** The content length as the service counts it (line ends normalised, trimmed). */
  get contentLength(): number {
    return normalizePlaybookContent(this.form.content).length;
  }

  save(): void {
    if (this.saving || this.editorLoading || !this.canEditForm) return;
    const isNew = !this.editing;
    const problem = playbookFormProblem(this.form, isNew);
    if (problem) {
      this.formError = problem;
      return;
    }
    this.formError = '';
    if (this.editing) {
      const body = buildPlaybookUpdate(this.editing, this.form);
      if (isEmptyUpdate(body)) {
        this.closeEditor();
        return;
      }
      this.saving = true;
      this.api.updatePlaybook(this.agent.id, this.editing.id, body).subscribe({
        next: () => this.saved(),
        error: (err) => this.saveFailed(err, 'Saving the playbook failed.'),
      });
      return;
    }
    this.saving = true;
    this.api.createPlaybook(this.agent.id, buildPlaybookCreate(this.form)).subscribe({
      next: () => this.saved(),
      error: (err) => this.saveFailed(err, 'Adding the playbook failed.'),
    });
  }

  toggleEnabled(p: Playbook): void {
    if (!this.canUpdate || this.toggling[p.id]) return;
    this.listError = '';
    this.toggling = { ...this.toggling, [p.id]: true };
    this.api.setPlaybookEnabled(this.agent.id, p.id, !p.enabled).subscribe({
      next: (updated) => {
        this.toggling = { ...this.toggling, [p.id]: false };
        const enabled = updated && typeof updated.enabled === 'boolean' ? updated.enabled : !p.enabled;
        this.playbooks = this.playbooks.map((x) => (x.id === p.id ? { ...x, enabled } : x));
      },
      error: (err) => {
        this.toggling = { ...this.toggling, [p.id]: false };
        this.listError = playbookErrorMessage(err, 'Changing the playbook failed.');
      },
    });
  }

  /** The shared delete confirmation (a property, so specs can answer it). */
  confirmDelete: () => Promise<{ isConfirmed: boolean }> = () => deleteDialog();

  async remove(p: Playbook): Promise<void> {
    if (!this.canDelete) return;
    const confirm = await this.confirmDelete();
    if (!confirm.isConfirmed) {
      return;
    }
    this.listError = '';
    this.api.deletePlaybook(this.agent.id, p.id).subscribe({
      next: () => {
        this.playbooks = this.playbooks.filter((x) => x.id !== p.id);
      },
      error: (err) => (this.listError = playbookErrorMessage(err, 'Deleting the playbook failed.')),
    });
  }

  private saved(): void {
    this.saving = false;
    this.closeEditor();
    this.load();
  }

  private saveFailed(err: any, fallback: string): void {
    this.saving = false;
    this.formError = playbookErrorMessage(err, fallback);
  }

  private toForm(p: Playbook): PlaybookForm {
    return {
      key: p.key || '',
      title: p.title || '',
      description: p.description || '',
      content: p.content || '',
      enabled: p.enabled !== false,
    };
  }

  private blankForm(): PlaybookForm {
    return { key: '', title: '', description: '', content: '', enabled: true };
  }
}
